import React, { useEffect, useState, useRef } from "react";
import { Send, Plus, MessageSquare, AlertTriangle } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth";
import { Button, Card, Input, Modal, Select, Field, Spinner, EmptyState, Badge, cx } from "../../components/ui";
import { toast } from "sonner";

export default function Messages() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const endRef = useRef(null);

  const loadConvs = () => api.get("/conversations").then((r) => setConvs(r.data)).catch(() => setConvs([]));
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/conversations/${active.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
    const i = setInterval(() => api.get(`/conversations/${active.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {}), 8000);
    return () => clearInterval(i);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const openNew = async () => {
    setNewOpen(true);
    try {
      const { data } = await api.get("/messages/recipients");
      setRecipients(data);
    } catch (err) { console.error("openNew recipients load failed", err); }
  };

  const send = async (recipientId) => {
    if (!body.trim() && !recipientId) return;
    try {
      if (recipientId) {
        const { data: msg } = await api.post("/messages", { recipient_id: recipientId, body: body || "Bonjour" });
        setBody("");
        const { data: convs2 } = await api.get("/conversations");
        setConvs(convs2);
        const created = convs2.find((c) => c.id === msg.conversation_id);
        if (created) { setActive(created); const m = await api.get(`/conversations/${created.id}/messages`); setMsgs(m.data); }
      } else {
        await api.post("/messages", { conversation_id: active.id, body });
        setBody(""); loadConvs();
        const m = await api.get(`/conversations/${active.id}/messages`); setMsgs(m.data);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="font-head font-bold text-navy text-2xl">{t("messages")}</h1>
        <Button className="ms-auto" size="sm" onClick={openNew} data-testid="new-conversation-btn"><Plus size={16} /> Nouveau</Button>
      </div>
      <div className="bg-orange-50 border border-orange-100 text-warn text-sm rounded-lg px-4 py-2.5 flex items-center gap-2">
        <AlertTriangle size={16} /> {t("emergency_notice")}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 h-[65vh]">
        <Card className="overflow-y-auto spina-scroll lg:col-span-1">
          {!convs ? <div className="p-6 flex justify-center"><Spinner /></div> :
            convs.length === 0 ? <EmptyState title={t("no_data")} /> :
            convs.map((c) => {
              const other = Object.entries(c.participant_names || {}).find(([id]) => id !== user.id);
              return (
                <button key={c.id} onClick={() => setActive(c)} data-testid={`conv-${c.id}`}
                  className={cx("w-full text-start px-4 py-3 border-b border-slate-100 hover:bg-vlg flex items-center gap-3", active?.id === c.id && "bg-teal-50")}>
                  <div className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center text-xs font-bold">{(other?.[1] || "?")[0]}</div>
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium text-navy truncate">{other?.[1] || "Conversation"}</div>
                    <div className="text-xs text-slate-400 truncate">{c.last_message}</div></div>
                  {c.unread > 0 && <Badge tone="alert">{c.unread}</Badge>}
                </button>
              );
            })}
        </Card>

        <Card className="lg:col-span-2 flex flex-col">
          {!active ? <div className="flex-1 flex items-center justify-center text-slate-400"><MessageSquare className="me-2" /> Sélectionnez une conversation</div> : (
            <>
              <div className="flex-1 overflow-y-auto spina-scroll p-4 space-y-2">
                {msgs.map((m) => (
                  <div key={m.id} className={cx("flex", m.sender_id === user.id ? "justify-end" : "justify-start")}>
                    <div className={cx("max-w-[75%] rounded-2xl px-3.5 py-2 text-sm", m.sender_id === user.id ? "bg-teal text-white" : "bg-slate-100 text-slate-700")}>
                      {m.body}<div className={cx("text-[10px] mt-1", m.sender_id === user.id ? "text-white/70" : "text-slate-400")}>{(m.created_at || "").slice(11, 16)}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-200 p-3 flex gap-2">
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("message_placeholder")} data-testid="msg-input"
                  onKeyDown={(e) => e.key === "Enter" && send()} />
                <Button onClick={() => send()} data-testid="msg-send"><Send size={16} /></Button>
              </div>
            </>
          )}
        </Card>
      </div>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Nouvelle conversation">
        <p className="text-sm text-slate-500 mb-3">Choisissez un destinataire :</p>
        <div className="space-y-2 max-h-80 overflow-y-auto spina-scroll">
          {recipients.filter((r) => r.id).map((r) => (
            <button key={r.id} onClick={() => { send(r.id); setNewOpen(false); }} data-testid={`recipient-${r.id}`}
              className="w-full text-start px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-teal-50 text-sm font-medium text-navy">{r.label}</button>
          ))}
          {recipients.filter((r) => r.id).length === 0 && <EmptyState title="Aucun destinataire" />}
        </div>
      </Modal>
    </div>
  );
}
